import { cx } from '@emotion/css';
import { Slider } from '@material-ui/core';
import { FieldLabelWrapper } from 'components/ui/molecules/FieldLabelWrapper/FieldLabelWrapper';
import { generateClassname, generateDtiAttribute } from 'helpers/helpers';
import { useCallback, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { useStyles } from './Range.styles';
import Thumb from './Thumb';
import { thumbHoveringParent } from './Thumb.styles';
import { RangeChangeHandler, RangeProps } from './types';

const Range = ({
  label,
  tooltipLabel,
  step,
  max,
  onChange,
  onChangeCommitted,
  valueLabelFormat,
  value,
  sliderPadding = '',
  min = 0,
  defaultValue = min,
  marks = false,
  disabled = false,
  contained = false,
  ...props
}: RangeProps) => {
  const [focused, setFocused] = useState(false);
  const [debouncedFocused, setDebouncedFocused] = useState(false);
  const rangeIdRef = useRef(generateClassname());
  const lastValueRef = useRef<number | number[]>();

  const classes = useStyles({ disabled, contained, focused: debouncedFocused, sliderPadding });

  useDebounce(() => setDebouncedFocused(focused), 13, [focused]);

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleMouseDown = () => {
    if (disabled) return;

    document.body.classList.add('mouse-grabbing');
  };

  const handleChange = useCallback<RangeChangeHandler>(
    (event, value) => {
      const lastValue = lastValueRef.current;
      const isArray = Array.isArray(lastValue) && Array.isArray(value);
      const isEqual = isArray ? lastValue[0] === value[0] && lastValue[1] === value[1] : lastValue === value;

      if (!isEqual) {
        onChange?.(event, value);
        lastValueRef.current = value;
      }
    },
    [onChange],
  );

  const handleChangeCommited = (event: React.ChangeEvent<{}>, value: number | number[]) => {
    document.body.classList.remove('mouse-grabbing');

    onChangeCommitted?.(event, value);
  };

  return (
    <div css={classes.root} {...generateDtiAttribute(props['data-tutorial-id'], 'range')}>
      <FieldLabelWrapper label={label} tooltipLabel={tooltipLabel} htmlFor={rangeIdRef.current} disabled={disabled}>
        <span
          css={classes.sliderWrapper}
          className={cx(debouncedFocused && 'm-range-focused', thumbHoveringParent)}
          onFocus={handleFocus}
          onBlur={handleBlur}>
          <Slider
            id={rangeIdRef.current}
            value={value}
            defaultValue={defaultValue}
            min={min}
            step={step}
            max={max}
            onChange={handleChange}
            onChangeCommitted={handleChangeCommited}
            getAriaValueText={valueLabelFormat as any}
            valueLabelFormat={valueLabelFormat}
            valueLabelDisplay="auto"
            marks={marks}
            disabled={disabled}
            ThumbComponent={Thumb}
            onMouseDown={handleMouseDown}
            {...generateDtiAttribute('slider')}
          />
        </span>
      </FieldLabelWrapper>
    </div>
  );
};

export default Range;
